import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Snackbar,
  Paper,
  useTheme
} from "@mui/material";
import { Delete as DeleteIcon, Share as ShareIcon } from "@mui/icons-material";
import {
  fetchMessagesAPI,
  deleteMessageAPI,
  resetUserAPI,
} from "../../services/ApiService";
import Confetti from "react-confetti";
import DeleteConfirmModal from "../common/modals/DeleteConfirmModal";
import { useTranslation } from "react-i18next";
import RefreshIcon from '@mui/icons-material/Refresh';

const LinkDisplay = ({ link }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const UserId = localStorage.getItem("userId");
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [open, setOpen] = useState(false)

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const messages = await fetchMessagesAPI(UserId);
      setMessages(messages);
    } catch (err) {
      setError(t('linkDisplay.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (link) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [link]);

  const deleteMessage = async (messageId) => {
    const isConfirmed = window.confirm(
      t('linkDisplay.deleteConfirm')
    );

    if (!isConfirmed) return;

    try {
      const success = await deleteMessageAPI(messageId);
      if (success) {
        setMessages((prevMessages) =>
          prevMessages.filter((message) => message.id !== messageId)
        );
      }
    } catch (err) {
      setError(t('linkDisplay.errors.deleteFailed'));
    }
  };

  const handleReset = async () => {
    try {
      const success = await resetUserAPI(UserId);
      if (success) {
        localStorage.removeItem("userId");
        localStorage.removeItem("link");
        window.location.href = "/";
      }
    } catch (err) {
      setError(t('linkDisplay.errors.resetFailed'));
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const handleWhatsAppShare = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
      `Check out my secret message link: ${link}`
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  useEffect(() => {
    fetchMessages();
  }, [UserId]);

  const handleRefreshMessages = () => {
    fetchMessages();
  }

  return (
    <>
      {showConfetti && <Confetti />}
      <Box
        sx={{
          maxWidth: 800,
          p: 4,
          mx: "auto",
          mt: 8,
          mb: 8,
          px: { xs: 2, sm: 4 },
        }}
      >
        <Paper
          sx={{
            borderRadius: "10px",
            border: "4px solid", // Border color will be handled by theme
            borderColor: "primary.main", // Use theme's primary color for border
            p: 4,
            backgroundColor: "background.paper", // Use theme's paper background color
          }}
        >
          <CardContent>
            <Typography
              variant="h5"
              align="center"
              sx={{
                color: "text.primary",
                fontWeight: "bold",
                fontSize: { xs: "1.5rem", sm: "2rem" },
              }}
              gutterBottom
            >
              {t('linkDisplay.title')}
            </Typography>
            <Typography
              variant="h5"
              align="center"
              color="text.primary"
              sx={{
                color: "text.primary",
                fontSize: { xs: "1rem", sm: "1.25rem" },
                fontFamily: "cursive",
              }}
              gutterBottom
            >
              {t('linkDisplay.subtitle')}
            </Typography>
            <TextField
              fullWidth
              value={link}
              InputProps={{
                readOnly: true,
              }}
              InputLabelProps={{ style: { color: "#fff" } }}
              inputProps={{
                style: {
                  color: "#fff",
                  fontSize: 18,
                  borderRadius: "8px",
                },
              }}
              sx={{

                mt: 4,
                mb: 3,
              }}
              variant="outlined"
            />
            <Button
              fullWidth
              onClick={handleCopyLink}
              variant="contained"
              color="primary"
              size="large"
              sx={{
                mb: 3,

                fontSize: { xs: "1rem", sm: "1.25rem" },
                fontWeight: "bold",
              }}
            >
              {copySuccess ? t('linkDisplay.copiedButton') : t('linkDisplay.copyButton')}
            </Button>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
              }}
            >
              <Button
                onClick={handleWhatsAppShare}
                variant="contained"
                startIcon={<ShareIcon />}
                sx={{
                  ...theme.components.MuiButton.styleOverrides.whatsappButton

                }}
              >
                {t('linkDisplay.whatsAppButton')}
              </Button>
              <Button
                onClick={() => setOpen(true)}
                variant="contained"
                color="error"
                sx={{
                  ...theme.components.MuiButton.styleOverrides.resetButton
                }}
              >
                {t('linkDisplay.resetButton')}
              </Button>
            </Box>
          </CardContent>
        </Paper>

        {loading ? (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Card sx={{ backgroundColor: "black", mt: 10 }}>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '100%' }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    color: (theme) => theme.palette.text.constant,
                  }}
                >
                  {t('linkDisplay.messagesBoard')}
                </Typography>

                <Button
                  variant="contained"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefreshMessages} // <- your refresh handler
                >
                  Refresh
                </Button>
              </Box>
              {error ? (
                <Typography color="error" align="center">
                  {error}
                </Typography>
              ) : messages.length > 0 ? (
                <List>
                  {messages.map((message, index) => (
                    <ListItem
                      key={message.id}
                      divider
                      sx={{
                        backgroundColor: "#1e293b",
                        borderRadius: "8px",
                        mb: 2,
                      }}
                    >
                      <ListItemText
                        primary={
                          <span style={{ color: "#fff", fontSize: "24px" }}>
                            {`${index + 1}. ${message.messages || "No message content"
                              }`}
                          </span>
                        }
                        secondary={
                          <span style={{ color: "gray", fontSize: "18px" }}>
                            {new Date(message.added_date).toISOString().split("T")[0]}
                          </span>
                        }
                        sx={{ color: "#fff" }}
                      />

                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => deleteMessage(message.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography align="center" sx={{ color: (theme) => theme.palette.text.constant, }}>
                  {t('linkDisplay.noMessages')}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        <Snackbar
          open={copySuccess}
          message={t('linkDisplay.copiedToast')}
          autoHideDuration={2000}
          onClose={() => setCopySuccess(false)}
        />
        <DeleteConfirmModal open={open} setOpen={setOpen} handleReset={handleReset} />
      </Box>
    </>
  );
};

export default LinkDisplay;
